import { type VariablesOf } from '@graphql-typed-document-node/core'
import { useAtomValue, useSetAtom } from 'jotai'
import { useResetAtom } from 'jotai/utils'
import React from 'react'
import { useTranslation } from 'react-i18next'
import * as R from 'remeda'

import { brandGroupIdAtom, surveyFilterAtom } from '~/atoms'
import { graphql } from '~/gql'
import { useSurveyQueryAvailableBrandGroups } from '~/hooks/useSurveyQuery'
import { useTabulate } from '~/hooks/useTabulate'
import { assertExists, findByPath, intersectionBy, pathOr } from '~/utils'

import {
  brandIdsAtom,
  columnCustomerAttributeIdsAtom,
  commentBySegmentDisplayTypeAtom,
  customerAttributeIdsAtom,
  isPendingTabulationAtom,
  isTabulatableAtom,
  isTabulationReadyAtom,
  questionIdsAtom,
  savedCommentBySegmentDisplayTypeAtom,
  tabulationDataAtom,
} from '../atoms'

const mutationDocument = graphql(`
  mutation CustomerAttributeAnalysisPage_TabulationMutation(
    $columnCustomerAttributeIds: [ID!]!
    $brands: [CountByCustomerAttributeBrandInput!]!
    $questionIds: [String!]!
    $customerAttributeIds: [ID!]!
    $filter: [[CustomerAttributeFilterItemInput!]!]!
  ) {
    count: countByCustomerAttributes(
      input: {
        byCustomerAttributeIds: $columnCustomerAttributeIds
        brands: $brands
        questions: $questionIds
        customerAttributeIds: $customerAttributeIds
        customFilter: { filter: $filter }
      }
    ) {
      awaitId
    }
  }
`)
type MutationVariables = VariablesOf<typeof mutationDocument>

const queryDocument = graphql(`
  query CustomerAttributeAnalysisPage_Tabulation($awaitId: String!) {
    count: countByCustomerAttributes(awaitId: $awaitId) {
      arrived
      result {
        ...CustomerAttributeAnalysisPage_TabulationQueryData
      }
    }
  }
`)

export const useTabulationQuery = () => {
  const { i18n } = useTranslation()
  const isTabulationReady = useAtomValue(isTabulationReadyAtom)
  const isPendingTabulation = useAtomValue(isPendingTabulationAtom)
  const isTabulatable = useAtomValue(isTabulatableAtom)
  const surveyFilter = useAtomValue(surveyFilterAtom)
  const brandGroupId = useAtomValue(brandGroupIdAtom)
  const columnCustomerAttributeIds = useAtomValue(
    columnCustomerAttributeIdsAtom,
  )
  const brandIds = useAtomValue(brandIdsAtom)
  const questionIds = useAtomValue(questionIdsAtom)
  const customerAttributeIds = useAtomValue(customerAttributeIdsAtom)
  const brandGroups = useSurveyQueryAvailableBrandGroups()

  const [tabulateState, tabulate] = useTabulate(mutationDocument, queryDocument)

  const setTabulationData = useSetAtom(tabulationDataAtom)
  const resetComment = useResetAtom(commentBySegmentDisplayTypeAtom)
  const resetSavedComment = useResetAtom(savedCommentBySegmentDisplayTypeAtom)

  const hasTabulatedRef = React.useRef(false)

  const tabulateIfNeeded = React.useCallback(async () => {
    if (!isTabulationReady || isPendingTabulation || !isTabulatable) {
      return
    }

    // t を使うと言語切り替えで参照が変わり useEffect が再実行され、
    // 再集計とコメントリセットが走ってしまうため、参照が安定な i18n を使う。
    assertExists(brandGroupId, i18n.t('assertion_failed'))

    const variables: MutationVariables = {
      columnCustomerAttributeIds,
      brands: R.pipe(
        brandGroups,
        findByPath('id', brandGroupId),
        pathOr('brands', []),
        intersectionBy(brandIds, R.prop('id'), R.identity()),
        R.map(({ number: brandNumber }) => ({ brandGroupId, brandNumber })),
      ),
      questionIds,
      customerAttributeIds,
      filter: surveyFilter?.filterGroups ?? [],
    }
    const result = (await tabulate(variables)) ?? undefined

    setTabulationData({
      result,
      columnCustomerAttributeIds,
      brandIds,
      questionIds,
      customerAttributeIds,
    })

    // 再集計時は古いコメントが新しいデータに残らないようリセットする。
    // 初回集計（URL 復元したコメントを保持）ではリセットしない。
    if (hasTabulatedRef.current) {
      resetComment()
      resetSavedComment()
    }
    hasTabulatedRef.current = true
  }, [
    brandGroupId,
    brandGroups,
    brandIds,
    columnCustomerAttributeIds,
    customerAttributeIds,
    i18n,
    isPendingTabulation,
    isTabulatable,
    isTabulationReady,
    questionIds,
    resetComment,
    resetSavedComment,
    setTabulationData,
    surveyFilter?.filterGroups,
    tabulate,
  ])

  React.useEffect(() => {
    tabulateIfNeeded()
  }, [tabulateIfNeeded])

  return tabulateState.loading
}
